import { isPlainObject } from 'lodash-es';
import { captureError } from '@leagueplatform/observability';
import type {
  ConfigEntity,
  ConfigEntityRootData,
  DomainEntity,
  EntityDetail,
  EntityRootData,
  RuleTemplateDataField,
} from '@web-config-app/core';
import { getFeatureFlag, isConfigEntity } from '@web-config-app/core';
import { parseEditorStateEntityMetadata } from '../parse-editor-state-entity-metadata/parse-editor-state-entity-metadata';

const COMPLETION_CRITERIA_FIELD_KEY = 'completionData.completionCriteria';
const CONFIG_ACTIVITY_DOMAIN_ENTITY_TYPE = 'config-activity';
const EMPTY_COMPLETION_CRITERIA_ERROR =
  'Completion criteria is empty or contains unresolved template placeholders while a rule template is selected. Complete the template inputs or remove the template selection before publishing.';
const GENERIC_EVENT_WITHOUT_CRITERIA_ERROR =
  'This activity completes on any task completion, so it needs a completion criteria to decide when it is done. Without one it will complete as soon as the member finishes any other task. Add a completion criteria, or change the event id to a specific one.';

/**
 * Thrown when the guard refuses a write. Distinct type because
 * `isRetryableError` reads a status-less error as a network fault and would
 * retry a refusal that cannot change.
 */
export class CompletionCriteriaGuardError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CompletionCriteriaGuardError';
  }
}

// An unsubstituted rule-template placeholder. Real property names are quoted
// (`"key":`), so a bare `{name}` in serialized JSONLogic can only be one.
const UNRESOLVED_PLACEHOLDER_PATTERN = /\{[A-Za-z_][A-Za-z0-9_]*\}/;

const getDomainEntityType = (
  entity: ConfigEntity | DomainEntity,
): string | undefined =>
  isConfigEntity(entity) ? entity.domainEntity.type : entity.type;

const isTemplateSelectedInField = (
  field: RuleTemplateDataField | undefined,
): boolean => {
  if (!field) return false;

  if (field.rulesTemplate?.templateId) {
    return true;
  }

  if (Array.isArray(field.nestedFields)) {
    return field.nestedFields.some(isTemplateSelectedInField);
  }

  if (field.nestedFields && typeof field.nestedFields === 'object') {
    return Object.values(field.nestedFields).some(isTemplateSelectedInField);
  }

  return false;
};

// Boundary data is untyped in practice, so walk it as an unknown record.
const readObjectProperty = (source: unknown, key: string): unknown => {
  if (!source || typeof source !== 'object') return undefined;
  return (source as Record<string, unknown>)[key];
};

const getCompletionData = (
  data: EntityRootData | ConfigEntityRootData | undefined,
): unknown => {
  const attributes = data?.attributes;
  if (!attributes || typeof attributes !== 'object') return undefined;

  const domainEntityAttributes = readObjectProperty(
    attributes,
    'domainEntityAttributes',
  );

  return (
    readObjectProperty(domainEntityAttributes, 'completionData') ??
    readObjectProperty(attributes, 'completionData')
  );
};

// Fires on any task completion. Lives on the entity, so unlike a rule-template
// selection it survives cloning, copy, and import.
//
// Whole, case-sensitive match, mirroring the backend's `eventIds ==
// TaskCompletedEventId`. A cased or comma-packed variant cannot auto-complete, so
// matching it would block an activity that is not at risk.
const GENERIC_COMPLETION_EVENT_ID = 'task_completed';

const hasGenericCompletionEvent = (completionData: unknown): boolean =>
  readObjectProperty(completionData, 'eventId') === GENERIC_COMPLETION_EVENT_ID;

// A rule is an object with at least one operator key. `true`, `6`, `[1,2]`,
// `'{ }'` all parse but name no operator, so they cannot decide completion —
// how the backend reads them too. Malformed JSON is left alone, also matching it.
const isNonRuleJson = (value: string): boolean => {
  try {
    const parsed = JSON.parse(value);
    return !isPlainObject(parsed) || Object.keys(parsed).length === 0;
  } catch {
    return false;
  }
};

// "Empty" means "cannot decide completion": absent, not a rule, or still holding
// an unresolved placeholder like `{campaignId}`, which never matches at runtime.
const isEmptyCompletionCriteria = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // An empty string does not parse; `'{}'` is caught by isNonRuleJson.
    if (trimmed === '') return true;
    if (isNonRuleJson(trimmed)) return true;
    return UNRESOLVED_PLACEHOLDER_PATTERN.test(trimmed);
  }
  if (typeof value === 'object') {
    if (Array.isArray(value)) return true;
    if (Object.keys(value as object).length === 0) return true;
    return UNRESOLVED_PLACEHOLDER_PATTERN.test(JSON.stringify(value));
  }
  // A raw non-string primitive — no operator, same as its stringified form.
  return true;
};

interface AssertCompletionCriteriaPublishableOptions {
  data: EntityRootData | ConfigEntityRootData | undefined;
  /**
   * Only used to derive the domain type, so it may be omitted when
   * `domainEntityType` is supplied.
   */
  entity?: ConfigEntity | DomainEntity;
  /**
   * Domain type override. Takes precedence over the type derived from
   * `entity` — useful when the caller has a per-invocation domain type
   * that differs from the entity handle's default.
   */
  domainEntityType?: string;
}

/**
 * Throws when a config-activity write would ship a `completionData.completionCriteria`
 * that cannot decide completion, in either state where that is known to be wrong:
 *
 * - a rule template is selected in `entityMetadata.editorState`, so a criteria was
 *   intended and is empty or unresolved;
 * - `completionData.eventId` fires on any task completion, so without a criteria the
 *   activity completes on the next task the member finishes.
 *
 * Only the second survives copy and import — `editorState` is client-side state, and
 * the action that clears the criteria clears it too.
 *
 * Silent otherwise: no criteria and no generic event is an ordinary click-through
 * activity. Bypassed by the `disableCompletionCriteriaGuard` flag, user-toggleable so
 * a misfire needs no redeploy.
 *
 * Throws are captured before propagating — the guard firing is the first live signal
 * of the underlying data-loss bug, every repro attempt having failed. `trippedBy`
 * records which arm caught it.
 */
export const assertCompletionCriteriaPublishable = ({
  data,
  entity,
  domainEntityType,
}: AssertCompletionCriteriaPublishableOptions): void => {
  if (getFeatureFlag('disableCompletionCriteriaGuard')) {
    return;
  }

  const effectiveDomainEntityType =
    domainEntityType ?? (entity ? getDomainEntityType(entity) : undefined);

  if (effectiveDomainEntityType !== CONFIG_ACTIVITY_DOMAIN_ENTITY_TYPE) {
    return;
  }

  const completionData = getCompletionData(data);
  const resolvedValue = readObjectProperty(
    completionData,
    'completionCriteria',
  );

  if (!isEmptyCompletionCriteria(resolvedValue)) {
    return;
  }

  const editorStateString = data?.attributes?.entityMetadata?.editorState;
  // parseEditorStateEntityMetadata reports its own parse failures.
  const editorState = data
    ? parseEditorStateEntityMetadata(data as EntityDetail)
    : undefined;

  const completionCriteriaField =
    editorState?.ruleTemplates?.fields?.[COMPLETION_CRITERIA_FIELD_KEY];

  const templateSelected = isTemplateSelectedInField(completionCriteriaField);
  const genericEvent = hasGenericCompletionEvent(completionData);

  if (!templateSelected && !genericEvent) {
    return;
  }

  // A selected template names something specific to fix, so it wins when both hold.
  const trippedBy = templateSelected
    ? `ruleTemplate${genericEvent ? '+genericEventId' : ''}`
    : 'genericEventId';

  const error = new CompletionCriteriaGuardError(
    templateSelected
      ? EMPTY_COMPLETION_CRITERIA_ERROR
      : GENERIC_EVENT_WITHOUT_CRITERIA_ERROR,
  );
  captureError(error, {
    errorName: 'CompletionCriteriaGuardBlockedPublish',
    context: {
      CompletionCriteriaGuard: {
        entityId: data?.id,
        domainEntityType: effectiveDomainEntityType,
        templateId: completionCriteriaField?.rulesTemplate?.templateId,
        trippedBy,
        // Shape is what's useful for triage, not the whole value.
        resolvedValuePreview:
          typeof resolvedValue === 'string'
            ? resolvedValue.slice(0, 200)
            : JSON.stringify(resolvedValue)?.slice(0, 200),
        editorStatePreview: editorStateString?.slice(0, 500),
      },
    },
  });
  throw error;
};
